import React, { useState, useEffect } from 'react';
// import { useAuth } from '../../contexts/AuthContext'; // Não é mais usado diretamente
import api from '../../services/api';
import { RefreshCw, Trash2, Loader2, ChevronLeft, ChevronRight, X } from 'lucide-react';
// import { Toast } from '../../components/Toast'; // Remover import do Toast local
import { useToast } from '../../contexts/ToastContext'; // Importar useToast do contexto

// Interface para os dados do snapshot recebidos da API
interface SnapshotData {
  id: string;
  magalu_id: string;
  volume_id_magalu: string;
  name: string;
  size: number;
  description?: string;
  state?: string;
  status?: string;
  type: string;
  encrypted: boolean;
  createdAt: string; // Data como string ISO
  updatedAt: string; // Data como string ISO
  volume?: { // Adicionar o objeto volume (opcional '?' por segurança)
    nome: string;
    ambiente?: { // Adicionar ambiente (opcional)
      nome: string;
    }
  }
}

// Interface para resposta paginada
interface PaginatedResponse {
  data: SnapshotData[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Adicionar tipo Ambiente
type Ambiente = any; 

export function VolumeBackupList() {
  const [snapshots, setSnapshots] = useState<SnapshotData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null); // Manter para erro inicial
  const [syncing, setSyncing] = useState(false);
  const [processingDeleteId, setProcessingDeleteId] = useState<string | null>(null); // Estado para botão de delete
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null); // Remover estado local do toast
  const { addToast } = useToast(); // Usar o addToast do contexto

  // --- Estados e Lógica de Filtros (Início) ---
  const [ambientes, setAmbientes] = useState<Ambiente[]>([]);
  const [selectedAmbiente, setSelectedAmbiente] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  useEffect(() => {
    const fetchAmbientes = async () => {
      try {
        const response = await api.get<{ data: Ambiente[] }>('/api/ambientes');
        if (response.data && Array.isArray(response.data.data)) {
          setAmbientes(response.data.data);
        } else {
          setAmbientes([]);
        }
      } catch (error) {
        console.error('Erro ao buscar ambientes:', error);
        setAmbientes([]);
      }
    };
    fetchAmbientes();
  }, []);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);
  // --- Estados e Lógica de Filtros (Fim) ---

  // Modificar fetchSnapshots para usar filtros
  useEffect(() => {
    fetchSnapshots(1);
  }, [selectedAmbiente, debouncedSearchTerm]); // Dependências

  const fetchSnapshots = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {
        page,
        pageSize
      };
      if (selectedAmbiente) {
        params.ambienteId = selectedAmbiente;
      }
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }

      const response = await api.get<PaginatedResponse>('/api/volume-snapshots', { params });
      setSnapshots(response.data.data);
      setTotalItems(response.data.total);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.page);
    } catch (err: any) {
      let errorMessage = 'Ocorreu um erro desconhecido';
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      if (errorMessage.includes("is not valid JSON")) {
        errorMessage = "Erro de comunicação com a API. Verifique o proxy do Vite e se o backend está rodando.";
      }
      setError(errorMessage);
      console.error("Erro ao buscar snapshots:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handler para mudar de página
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    fetchSnapshots(page);
  };

  // Handlers para mudança nos filtros
  const handleAmbienteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAmbiente(event.target.value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Handler para o botão de sincronizar (atualizado)
  const handleSync = async () => {
    setSyncing(true);
    addToast({ type: 'info', title: 'Iniciando sincronização de snapshots...' }); // Toast inicial
    setError(null); // Limpar erro de carregamento anterior
    try {
      const response = await api.post('/api/volume-snapshots/sync');

      if (response.status === 200) { // Checar status explicitamente
        addToast({ type: 'success', title: 'Sincronização de snapshots concluída!' });
        await fetchSnapshots(1); // Rebusca os dados após sincronizar, voltando para página 1
      } else {
        // Erro retornado pela API (status != 200)
        console.error('Falha na sincronização, status:', response.status, response.data);
        const errorDesc = response.data?.message || response.data?.error || `Status: ${response.status}`;
        addToast({ type: 'error', title: 'Falha na sincronização', description: errorDesc });
      }

    } catch (err: any) {
      // Erro de rede ou outro erro inesperado
      console.error("Erro ao sincronizar snapshots:", err);
      if (!err.response) {
        // Erro de rede
        addToast({ type: 'error', title: 'Erro de rede', description: 'Não foi possível conectar ao servidor para sincronizar.' });
      } else {
        // Outro erro (inesperado, pois status != 200 foi tratado no try)
        const errorDesc = err.response.data?.message || err.response.data?.error || err.message || 'Erro desconhecido';
        addToast({ type: 'error', title: 'Erro ao sincronizar', description: errorDesc });
      }
    } finally {
      setSyncing(false);
    }
  };

  // Handler para o botão de deletar
  const handleDelete = async (id: string, name: string) => {
    if (processingDeleteId) return; // Evita cliques múltiplos

    if (!window.confirm(`Tem certeza que deseja excluir o snapshot "${name}" do MAGALUCLOUD? Esta ação não pode ser desfeita.`)) {
      return;
    }

    setProcessingDeleteId(id);
    try {
      const response = await api.delete(`/api/volume-snapshots/${id}`);
      if (response.status === 204) {
        addToast({ type: 'success', title: 'Registro de snapshot excluído com sucesso!' });
        await fetchSnapshots(currentPage); // Atualiza a lista na página atual
      } else {
        // Tratamento de erro caso a API retorne algo diferente de 204 (ex: 404)
        const errorDesc = response.data?.message || `Erro ${response.status}`;
        addToast({ type: 'error', title: 'Erro ao excluir registro', description: errorDesc });
      }
    } catch (err: any) {
      console.error("Erro ao excluir registro de snapshot:", err);
      const errorDesc = err.response?.data?.message || err.message || "Erro desconhecido";
      addToast({ type: 'error', title: 'Erro ao excluir registro', description: errorDesc });
    } finally {
      setProcessingDeleteId(null);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('pt-BR');
    } catch {
      return dateString; // Retorna a string original se a data for inválida
    }
  };

  // Função para traduzir Estado do Snapshot
  const formatEstado = (estado: string | null | undefined): string => {
    if (!estado) return 'Desconhecido';
    const map: Record<string, string> = {
      available: 'Disponível',
      creating: 'Criando',
      deleting: 'Excluindo',
      error: 'Erro',
      pending: 'Pendente',
      new: 'Novo',
      creating_error_quota: 'Erro ao criar snapshot por quota excedida'
      // Adicionar outros estados se necessário
    };
    // Converter para minúsculas para correspondência insensível a maiúsculas/minúsculas
    return map[estado.toLowerCase()] || estado;
  };

  // Função para traduzir Status do Snapshot
  const formatStatus = (status: string | null | undefined): string => {
    if (!status) return 'Desconhecido';
    const map: Record<string, string> = {
      completed: 'Concluído',
      pending: 'Pendente',
      error: 'Erro',
      provisioning: 'Provisionando'
      // Adicionar outros status se necessário
    };
    return map[status.toLowerCase()] || status;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Remover renderização do Toast local */}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Volume Backups</h1>
        {/* Botão de Sincronizar */}
        <button
          onClick={handleSync}
          disabled={syncing || loading}
          // Aplicar as classes do botão da tela de Volumes
          className={`flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${syncing ? 'animate-pulse' : ''}`}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} /> {/* Ajustar tamanho do ícone se necessário (w-4 h-4) */}
          {syncing ? 'Sincronizando...' : 'Sincronizar MGC'} {/* Renomear texto */}
        </button>
      </div>

      {/* Linha de Filtros */}
      <div className="flex gap-4 mb-6 items-end">
          {/* Filtro de Busca com Label */} 
          <div className="w-64">
            <label htmlFor="vol-snapshot-search" className="block text-sm font-medium text-gray-700 mb-1">
              Buscar por nome (snapshot/volume)
            </label>
            <div className="relative">
              <input
                type="text"
                id="vol-snapshot-search"
                placeholder="Digite o nome..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 pr-8"
                disabled={loading || syncing}
              />
              {searchTerm && (
                <button 
                  type="button"
                  onClick={() => setSearchTerm('')} 
                  className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-gray-600"
                  title="Limpar busca"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Filtro de Ambiente com Label */} 
          <div className="w-64">
            <label htmlFor="vol-snapshot-ambiente-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Ambiente
            </label>
            <div className="relative">
              <select
                id="vol-snapshot-ambiente-filter"
                value={selectedAmbiente}
                onChange={handleAmbienteChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 pr-12 appearance-none"
                disabled={loading || syncing}
              >
                <option value="">Todos Ambientes</option>
                {ambientes.map((ambiente) => (
                  <option key={ambiente.id} value={ambiente.id}>
                    {ambiente.nome}
                  </option>
                ))}
              </select>
              {selectedAmbiente && (
                <button 
                  type="button"
                  onClick={() => setSelectedAmbiente('')} 
                  className="absolute inset-y-0 right-7 flex items-center pr-2 text-gray-400 hover:text-gray-600"
                  title="Limpar seleção"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
              </div>
            </div>
          </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        {loading && <p className="p-6 text-center text-gray-600">Carregando snapshots...</p>}
        {/* Exibir erro geral de carregamento inicial, se houver */}
        {error && !syncing && <p className="p-6 text-center text-red-600 font-semibold">Erro ao carregar: {error}</p>}
        {!loading && !error && (
          <div className="overflow-x-auto">
            {snapshots.length === 0 ? (
              <p className="p-6 text-center text-gray-600">Nenhum snapshot encontrado.</p>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ambiente</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Volume</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tamanho</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criado em</th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {snapshots.map((snapshot) => {
                      const isDeleting = processingDeleteId === snapshot.id;
                      return (
                        <tr key={snapshot.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{snapshot.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{snapshot.volume?.ambiente?.nome ?? 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{snapshot.volume?.nome ?? 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{`${snapshot.size} GB`}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${snapshot.state === 'available' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {formatEstado(snapshot.state)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${snapshot.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                              {formatStatus(snapshot.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(snapshot.createdAt)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleDelete(snapshot.id, snapshot.name)}
                              disabled={isDeleting || syncing} // Desabilitar se estiver deletando ou sincronizando
                              className={`text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed p-1 rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50`}
                              title="Excluir no magalucloud"
                            >
                              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                
                {/* Paginação */}
                <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Próximo
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Mostrando <span className="font-medium">{snapshots.length > 0 ? (currentPage - 1) * pageSize + 1 : 0}</span> a <span className="font-medium">{Math.min(currentPage * pageSize, totalItems)}</span> de <span className="font-medium">{totalItems}</span> backups
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Paginação">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage <= 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Anterior</span>
                          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                        </button>
                        
                        {/* Páginas numéricas */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNum = Math.max(1, Math.min(
                            totalPages - 4, 
                            currentPage - 2
                          )) + i;
                          
                          if (pageNum <= totalPages) {
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`relative inline-flex items-center px-4 py-2 border ${currentPage === pageNum 
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' 
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                } text-sm font-medium`}
                              >
                                {pageNum}
                              </button>
                            );
                          }
                          return null;
                        })}
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Próximo</span>
                          <ChevronRight className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 